/**
 * <s-nav-columns> — three-column navigation drawer.
 *
 * Markup contract:
 *  - [data-nav-col-target="ID"] buttons in column 1 select a category.
 *  - [data-nav-col-panel="ID"] panels in column 2 list that category's links.
 *  - [data-nav-col-sub="SUB_ID"] items in column 2 show picture SUB_ID while hovered/focused.
 *  - [data-nav-col-image="ID"] pictures in column 3 (desktop only).
 *  - [data-nav-col-back] buttons return to column 1 on small screens.
 *  - [data-nav-col-empty] is shown when no category is selected.
 *
 * Wide screens (min-width: 1024px): hovering or pressing an item selects it and
 * the first category is selected when the drawer opens. Small screens: pressing
 * an item slides column 2 over column 1 (root gets [data-open]).
 */
const wide = window.matchMedia('(min-width: 1024px)');
const canHover = window.matchMedia('(hover: hover)');

class NavColumns extends HTMLElement {
  connectedCallback() {
    this.targets = [...this.querySelectorAll('[data-nav-col-target]')];
    this.onClick = this.onClick.bind(this);
    this.onPointerOver = this.onPointerOver.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onPointerLeavePanel = () => this.showImage(this.active);
    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('focusin', this.onFocusIn);
    for (const panel of this.querySelectorAll('[data-nav-col-panel]')) {
      panel.addEventListener('pointerleave', this.onPointerLeavePanel);
    }

    this.dialog = this.closest('dialog');
    if (this.dialog) {
      this.onDialogToggle = () => {
        if (!this.dialog.open) return;
        this.removeAttribute('data-open');
        // An opener with [data-nav-open="ID"] asks for a specific category (e.g. brands).
        const requested = this.dialog.dataset.navOpen;
        delete this.dialog.dataset.navOpen;
        if (requested && this.querySelector(`[data-nav-col-panel="${requested}"]`)) {
          this.select(requested);
          if (!wide.matches) this.setAttribute('data-open', '');
        } else if (wide.matches && this.targets[0]) this.select(this.targets[0].dataset.navColTarget);
        else this.select(null);
      };
      this.dialog.addEventListener('toggle', this.onDialogToggle);
    }
    if (wide.matches && this.targets[0]) this.select(this.targets[0].dataset.navColTarget);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('focusin', this.onFocusIn);
    for (const panel of this.querySelectorAll('[data-nav-col-panel]')) {
      panel.removeEventListener('pointerleave', this.onPointerLeavePanel);
    }
    if (this.dialog) this.dialog.removeEventListener('toggle', this.onDialogToggle);
  }

  select(id) {
    this.active = id;
    for (const button of this.targets) {
      button.setAttribute('aria-expanded', String(button.dataset.navColTarget === id));
    }
    for (const panel of this.querySelectorAll('[data-nav-col-panel]')) {
      panel.classList.toggle('hidden', panel.dataset.navColPanel !== id);
    }
    this.showImage(id);
    const empty = this.querySelector('[data-nav-col-empty]');
    if (empty) empty.classList.toggle('lg:block', !id);
  }

  /** Shows the picture whose [data-nav-col-image] matches id; hides the rest. */
  showImage(id) {
    for (const image of this.querySelectorAll('[data-nav-col-image]')) {
      const on = image.dataset.navColImage === id;
      image.classList.toggle('opacity-0', !on);
      image.classList.toggle('invisible', !on);
    }
  }

  onClick(event) {
    const back = event.target.closest('[data-nav-col-back]');
    if (back) {
      this.removeAttribute('data-open');
      const opener = this.targets.find((b) => b.dataset.navColTarget === this.active);
      if (opener) opener.focus();
      return;
    }
    const button = event.target.closest('[data-nav-col-target]');
    if (!button) return;
    const id = button.dataset.navColTarget;
    this.select(id);
    const panel = this.querySelector(`[data-nav-col-panel="${id}"]`);
    if (!wide.matches) {
      this.setAttribute('data-open', '');
      const backButton = panel && panel.querySelector('[data-nav-col-back]');
      if (backButton) backButton.focus({ preventScroll: true });
      if (panel && panel.parentElement) panel.parentElement.scrollTop = 0;
    } else if (event.detail === 0) {
      // Keyboard activation: move focus into the category list.
      const firstLink = panel && panel.querySelector('a');
      if (firstLink) firstLink.focus();
    }
  }

  onPointerOver(event) {
    if (!wide.matches || !canHover.matches) return;
    const button = event.target.closest('[data-nav-col-target]');
    if (button) {
      if (button.dataset.navColTarget !== this.active) this.select(button.dataset.navColTarget);
      return;
    }
    const sub = event.target.closest('[data-nav-col-sub]');
    if (sub) this.showImage(sub.dataset.navColSub);
  }

  onFocusIn(event) {
    if (!wide.matches) return;
    const sub = event.target.closest('[data-nav-col-sub]');
    this.showImage(sub ? sub.dataset.navColSub : this.active);
  }
}

if (!customElements.get('s-nav-columns')) customElements.define('s-nav-columns', NavColumns);

// Openers such as the header "Brands" button carry [data-nav-open="ID"] next to
// commandfor/command="show-modal"; remember the requested panel on the target
// dialog before it opens so the toggle handler can select it.
document.addEventListener(
  'click',
  (event) => {
    const opener = event.target instanceof Element ? event.target.closest('[data-nav-open][commandfor]') : null;
    if (!opener) return;
    const dialog = document.getElementById(opener.getAttribute('commandfor'));
    if (dialog) dialog.dataset.navOpen = opener.dataset.navOpen;
  },
  true,
);
